import {Observable, of, OperatorFunction, pipe} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {isSmartSecurityKey, SmartSecurityService} from '../../../components/smart-security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {SubscriptionSubject} from '../../../components/streaming/subscription-streaming.service';
import {SubscriptionSubjectType} from '../../../components/streaming/subscription-subject-type';
import {Exchange} from '../../../enums/exchange';
import {ArrayEncodedMdEntry} from '../../../models/common-ui-market-data/array-encoded-md-entry';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage,
    ArrayEncodedMdUpdateMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {MarketDataEntryType} from '../../../models/common-ui-market-data/market-data.enums';
import {Security} from '../../../models/security';
import {formatSecurityKey, parseSecurityKey, SecurityKey} from '../../../models/security-key';
import {MdSubscriptionSubject} from './md-subscription-subject';

enum SmartMdWebSocketMessageType {
    SMART_MD_SNAPSHOT = 'SMART_MD_SNAPSHOT',
    SMART_MD_UPDATE   = 'SMART_MD_UPDATE'
}

interface SmartMdWebSocketMessage extends WebSocketMessage {
    messageType: SmartMdWebSocketMessageType;
    data: ArrayEncodedMdMessage[];
}

interface SmartMdSnapshotWebSocketMessage extends SmartMdWebSocketMessage {
    messageType: SmartMdWebSocketMessageType.SMART_MD_SNAPSHOT;
    data: ArrayEncodedMdSnapshotMessage[];
}

interface SmartMdUpdateWebSocketMessage extends SmartMdWebSocketMessage {
    messageType: SmartMdWebSocketMessageType.SMART_MD_UPDATE;
    data: ArrayEncodedMdUpdateMessage[];
}

type MdEntriesDict = Partial<{[mdEntryType in MarketDataEntryType]: ArrayEncodedMdEntry[]}>;

export interface SecurityMarketData {
    mdReqId: string;
    totalTradeVolume: number;
    securityIncrementId: number;
    mdEntriesDict: MdEntriesDict;
    security: Security;
    securityKeyStr: string;
    mdEntryUpdates: ArrayEncodedMdEntry[];
}

export class SmartMdSubscriptionSubject extends MdSubscriptionSubject {

    public smartType: SubscriptionSubjectType<SecurityMarketData>;

    protected smartAggregationSuffix: string;

    private aggregatedSmartMessageTypeToCommonDict: {[aggregatedMessageType: string]: SmartMdWebSocketMessageType};
    private commonSmartMessageTypeToAggregatedDict: {[commonMessageType in SmartMdWebSocketMessageType]: string};

    constructor(
        protected override aggregationScale: number,
        protected smartSecurityService: SmartSecurityService
    ) {
        super(aggregationScale, smartSecurityService);
    }

    protected override createSubjectType(): void {
        super.createSubjectType();

        this.smartType = {
            name: `SMART_MARKET_DATA${this.smartAggregationSuffix}`,
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: `SUBSCRIBE_SMART_MD${this.smartAggregationSuffix}`,
                UNSUBSCRIBE: `UNSUBSCRIBE_SMART_MD${this.smartAggregationSuffix}`
            },
            messagePipe: pipe(
                filter((webSocketMessage: WebSocketMessage) =>
                    !!this.aggregatedSmartMessageTypeToCommonDict[webSocketMessage.messageType]),

                mergeMap((mdWebSocketMessage: SmartMdWebSocketMessage): Observable<SecurityMarketData> => {
                    const mdMessageContainers = this.readSmartMdWebSocketMessage(mdWebSocketMessage);

                    return of(...mdMessageContainers);
                })
            ),
            subjectPipeFactory: subject => this.createSmartSubjectPipe(subject),
            deprecateSubject: subject => this.deprecateSmartSubject(subject)
        };
    }

    protected override createWebSocketMessageTypes(): void {
        super.createWebSocketMessageTypes();

        const smartSnapshotMessage = [SmartMdWebSocketMessageType.SMART_MD_SNAPSHOT, this.smartAggregationSuffix]
            .join('');
        const smartUpdateMessage = [SmartMdWebSocketMessageType.SMART_MD_UPDATE, this.smartAggregationSuffix]
            .join('');

        this.aggregatedSmartMessageTypeToCommonDict = {
            [smartSnapshotMessage]: SmartMdWebSocketMessageType.SMART_MD_SNAPSHOT,
            [smartUpdateMessage]:   SmartMdWebSocketMessageType.SMART_MD_UPDATE
        };

        this.commonSmartMessageTypeToAggregatedDict = {
            [SmartMdWebSocketMessageType.SMART_MD_SNAPSHOT]: smartSnapshotMessage,
            [SmartMdWebSocketMessageType.SMART_MD_UPDATE]:   smartUpdateMessage
        };
    }

    protected override resolveReceivedSecurity<T extends SecurityKey | Security>(security: T): T {
        return isSmartSecurityKey(security)
            ? {
                ...security,
                ...this.getSmartSecurityKeyFromJoinedKeyStr(security.symbol)
            }
            : security;
    }

    protected override updateAggregationSuffix(): void {
        super.updateAggregationSuffix();

        this.smartAggregationSuffix = `#${this.aggregationScale || 1}`;
    }

    protected createSmartSubjectPipe(
        joinedSubject: SubscriptionSubject
    ): OperatorFunction<SecurityMarketData, SecurityMarketData> {
        const smartSubject = this.convertJoinedSubjectToSmart(joinedSubject);

        return this.createSubjectPipe(smartSubject);
    }

    protected deprecateSmartSubject(joinedSubject: SubscriptionSubject): void {
        const smartSubject = this.convertJoinedSubjectToSmart(joinedSubject);

        this.deprecateSubject(smartSubject);
    }

    private readSmartMdWebSocketMessage(mdWebSocketMessage: SmartMdWebSocketMessage): SecurityMarketData[] {
        let securitiesMd: SecurityMarketData[];
        const commonMessageType: SmartMdWebSocketMessageType =
                  this.aggregatedSmartMessageTypeToCommonDict[mdWebSocketMessage.messageType];

        switch (commonMessageType) {
            case SmartMdWebSocketMessageType.SMART_MD_SNAPSHOT:
                securitiesMd = this.readMdSnapshot(mdWebSocketMessage.data as ArrayEncodedMdSnapshotMessage[]);
                break;
            case SmartMdWebSocketMessageType.SMART_MD_UPDATE:
                securitiesMd = this.readMdUpdate(mdWebSocketMessage.data as ArrayEncodedMdUpdateMessage[]);
                break;
        }

        return securitiesMd;
    }

    private convertJoinedSubjectToSmart(joinedSubject: SubscriptionSubject): SubscriptionSubject {
        return joinedSubject.map(
            joinedSecurityKeyStr => formatSecurityKey(
                this.getSmartSecurityKeyFromJoinedKeyStr(joinedSecurityKeyStr)
            )
        );
    }

    private getSmartSecurityKeyFromJoinedKeyStr(joinedSecurityKeyStr: string): SecurityKey {
        const firstSecurityKeyStr = joinedSecurityKeyStr.split(',')[0];

        return {
            ...parseSecurityKey(firstSecurityKeyStr),
            securityExchange: Exchange.SMART
        };
    }

}
